/**
 * Celebration overlays (sparks, level up, stickers, …) are loaded only when needed — the start screen does not carry
 * them (docs/PERF.md). A lesson preloads them on mount, and once loaded they render synchronously (no Suspense
 * round-trip), so the first overlay appears as fast as before. The queue logic lives in CelebrationQueue.tsx.
 */
import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { usePresentationQueue } from './presenter';

let Loaded: ComponentType | null = null;
let loading: Promise<ComponentType> | null = null;
const load = () =>
  (loading ??= import('./CelebrationQueue').then((m) => {
    Loaded = m.CelebrationQueue;
    return m.CelebrationQueue;
  }));
const LazyQueue = lazy(() => load().then((c) => ({ default: c })));
/** Warm the overlays (a lesson is open / the app is idle) so the first celebration shows without a wait. */
export const preloadCelebrations = () => void load().catch(() => (loading = null));

export function CelebrationHost() {
  const has = usePresentationQueue((s) => s.items.length > 0);
  const [, setReady] = useState(!!Loaded);
  useEffect(() => {
    if (has && !Loaded) void load().then(() => setReady(true));
  }, [has]);
  if (!has) return null;
  if (Loaded) return <Loaded />;
  return (
    <Suspense fallback={null}>
      <LazyQueue />
    </Suspense>
  );
}
